import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = dismissed ? new Date(dismissed) : null;
    const hoursSinceDismissed = dismissedTime 
      ? (new Date() - dismissedTime) / (1000 * 60 * 60) 
      : 999;

    // Show prompt again after 24 hours
    if (hoursSinceDismissed < 24) {
      return;
    }

    // Listen for beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a short delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show custom install guide
    if (iOS) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    }

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      if (isIOS) {
        setShowIOSGuide(true);
      }
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('Install error:', error);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  // Don't show if already installed or prompt not available
  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-indigo-950 to-indigo-900 text-white shadow-lg animate-slide-up">
        <div className="max-w-lg mx-auto">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-indigo-950" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg">Instal SIGAP GURU</h3>
              <p className="text-indigo-200 text-sm mt-1">
                Instal aplikasi untuk pengalaman lebih baik dan akses offline yang lebih stabil.
              </p>
              
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={handleInstall}
                  className="bg-amber-500 hover:bg-amber-400 text-indigo-950 font-semibold"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isIOS ? 'Cara Instal' : 'Instal Sekarang'}
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="ghost"
                  className="text-indigo-200 hover:text-white hover:bg-indigo-800"
                  size="sm"
                >
                  Nanti Saja
                </Button>
              </div>
            </div>
            
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="icon"
              className="text-indigo-300 hover:text-white hover:bg-indigo-800 flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* iOS Install Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-indigo-100 flex items-center justify-center">
                <Smartphone className="w-8 h-8 text-indigo-600" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Instal di iPhone/iPad
              </h3>
              
              <p className="text-gray-600 mb-6">
                Ikuti langkah berikut untuk menginstal SIGAP GURU:
              </p>
              
              <div className="text-left space-y-4 mb-6">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-indigo-600">1</span>
                  </div>
                  <p className="text-gray-700">
                    Ketuk ikon <strong>Share</strong> (kotak dengan panah ke atas) di Safari
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-indigo-600">2</span>
                  </div>
                  <p className="text-gray-700">
                    Scroll ke bawah dan ketuk <strong>"Add to Home Screen"</strong>
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-indigo-600">3</span>
                  </div>
                  <p className="text-gray-700">
                    Ketuk <strong>"Add"</strong> untuk menyelesaikan instalasi
                  </p>
                </div>
              </div>
              
              <Button
                onClick={handleDismiss}
                className="w-full bg-indigo-950 hover:bg-indigo-900"
              >
                Mengerti
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
